import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  coreServicesApi, 
  type PermissionCheck, 
  type ChannelPermissionCheck, 
  type Permission,
  type GrantPermissionRequest,
  type RevokePermissionRequest
} from '../lib/api/core-services.js';

// Query keys for Permissions data
export const permissionsKeys = {
  all: ['permissions'] as const,
  check: (params: PermissionCheck) => [...permissionsKeys.all, 'check', params] as const,
  channelCheck: (params: ChannelPermissionCheck) => [...permissionsKeys.all, 'channel-check', params] as const,
  userLevel: (userId: string, guildId: string) => [...permissionsKeys.all, 'user-level', userId, guildId] as const,
  batchCheck: (checks: PermissionCheck[]) => [...permissionsKeys.all, 'batch-check', checks] as const,
  guild: (guildId: string) => [...permissionsKeys.all, 'guild', guildId] as const,
};

// Query keys for Discord data
export const discordKeys = {
  all: ['discord'] as const,
  guilds: () => [...discordKeys.all, 'guilds'] as const,
  guildMembers: (guildId: string, search?: string) => [...discordKeys.all, 'guild-members', guildId, search] as const,
  guildChannels: (guildId: string) => [...discordKeys.all, 'guild-channels', guildId] as const,
};

// Auth hook
export const useAuth = () => {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => coreServicesApi.auth.me(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Permission check hook
export const usePermissionCheck = (params: PermissionCheck, enabled = true) => {
  return useQuery({
    queryKey: permissionsKeys.check(params),
    queryFn: async () => {
      const response = await coreServicesApi.permissions.check(params);
      return response;
    },
    enabled: enabled && !!params.userId && !!params.guildId && !!params.botName,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Channel permission check hook
export const useChannelPermissionCheck = (params: ChannelPermissionCheck, enabled = true) => {
  return useQuery({
    queryKey: permissionsKeys.channelCheck(params),
    queryFn: async () => {
      const response = await coreServicesApi.permissions.checkChannel(params);
      return response;
    },
    enabled: enabled && !!params.guildId && !!params.channelId && !!params.botName,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

// User level check hook
export const useUserLevel = (userId: string, guildId: string, enabled = true) => {
  return useQuery({
    queryKey: permissionsKeys.userLevel(userId, guildId),
    queryFn: async () => {
      const response = await coreServicesApi.permissions.getUserLevel(userId, guildId);
      return response;
    },
    enabled: enabled && !!userId && !!guildId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Batch permission check hook
export const useBatchPermissionCheck = (checks: PermissionCheck[], enabled = true) => {
  return useQuery({
    queryKey: permissionsKeys.batchCheck(checks),
    queryFn: async () => {
      const response = await coreServicesApi.permissions.batchCheck(checks);
      return response;
    },
    enabled: enabled && checks.length > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Guild permissions hook
export const useGuildPermissions = (guildId: string, enabled = true) => {
  return useQuery({
    queryKey: permissionsKeys.guild(guildId),
    queryFn: async () => {
      const response = await coreServicesApi.permissions.getForGuild(guildId);
      return response;
    },
    enabled: enabled && !!guildId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Discord data hooks
export const useDiscordGuilds = () => {
  return useQuery({
    queryKey: discordKeys.guilds(),
    queryFn: async () => {
      const response = await coreServicesApi.discord.getGuilds();
      return response;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useDiscordGuildMembers = (guildId: string, search?: string, enabled = true) => {
  return useQuery({
    queryKey: discordKeys.guildMembers(guildId, search),
    queryFn: async () => {
      const response = await coreServicesApi.discord.getGuildMembers(guildId, search);
      return response;
    },
    enabled: enabled && !!guildId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useDiscordGuildChannels = (guildId: string, enabled = true) => {
  return useQuery({
    queryKey: discordKeys.guildChannels(guildId),
    queryFn: async () => {
      const response = await coreServicesApi.discord.getGuildChannels(guildId);
      return response;
    },
    enabled: enabled && !!guildId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Permission operations hook
export const usePermissionOperations = () => {
  const queryClient = useQueryClient();

  const grantPermission = useMutation({
    mutationFn: (request: GrantPermissionRequest) =>
      coreServicesApi.permissions.grant(request),
    onSuccess: (data, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: permissionsKeys.guild(variables.guildId) });
      queryClient.invalidateQueries({ queryKey: permissionsKeys.all });
    },
  });

  const revokePermission = useMutation({
    mutationFn: (request: RevokePermissionRequest) =>
      coreServicesApi.permissions.revoke(request),
    onSuccess: () => {
      // Invalidate all permission queries since we don't know which guild was affected
      queryClient.invalidateQueries({ queryKey: permissionsKeys.all });
    },
  });

  const updatePermission = useMutation({
    mutationFn: (permission: Partial<Permission>) =>
      coreServicesApi.permissions.update(permission),
    onSuccess: (data, variables) => {
      // Invalidate related queries
      if (variables.guildId) {
        queryClient.invalidateQueries({ queryKey: permissionsKeys.guild(variables.guildId) });
      }
      // Invalidate all permission checks for safety
      queryClient.invalidateQueries({ queryKey: permissionsKeys.all });
    },
  });

  return {
    grantPermission,
    revokePermission,
    updatePermission,
    isLoading: grantPermission.isPending || revokePermission.isPending || updatePermission.isPending,
    error: grantPermission.error || revokePermission.error || updatePermission.error,
  };
};

// Utility hook for common permission levels
export const useCommonPermissions = (userId: string, guildId: string) => {
  const hotelBullCheck = usePermissionCheck({ 
    userId, 
    guildId, 
    botName: 'hotel-bull', 
    requiredLevel: 'admin' 
  });
  
  const praiseKinkCheck = usePermissionCheck({ 
    userId, 
    guildId, 
    botName: 'praise-kink', 
    requiredLevel: 'admin' 
  });
  
  const dungeonCheck = usePermissionCheck({ 
    userId, 
    guildId, 
    botName: 'dungeon', 
    requiredLevel: 'admin' 
  });

  const valerieCheck = usePermissionCheck({
    userId,
    guildId,
    botName: 'valerie',
    requiredLevel: 'admin'
  });

  const userLevel = useUserLevel(userId, guildId);

  return {
    hotelBull: hotelBullCheck,
    praiseKink: praiseKinkCheck,
    dungeon: dungeonCheck,
    valerie: valerieCheck,
    userLevel,
    isLoading: hotelBullCheck.isLoading || praiseKinkCheck.isLoading || dungeonCheck.isLoading || valerieCheck.isLoading || userLevel.isLoading,
    error: hotelBullCheck.error || praiseKinkCheck.error || dungeonCheck.error || valerieCheck.error || userLevel.error,
  };
};

// Utility function to get permission level display info
export const getPermissionLevelInfo = (level: 'god_mode' | 'admin' | 'target' | 'user') => {
  switch (level) {
    case 'god_mode':
      return {
        label: 'God Mode',
        color: 'text-purple-400',
        bgColor: 'bg-purple-100',
        description: 'Full system access'
      };
    case 'admin':
      return {
        label: 'Admin',
        color: 'text-red-400',
        bgColor: 'bg-red-100',
        description: 'Administrative privileges'
      };
    case 'target':
      return {
        label: 'Target',
        color: 'text-yellow-400',
        bgColor: 'bg-yellow-100',
        description: 'Target user privileges'
      };
    case 'user':
      return {
        label: 'User',
        color: 'text-green-400',
        bgColor: 'bg-green-100',
        description: 'Basic user privileges'
      };
    default:
      return {
        label: 'Unknown',
        color: 'text-gray-400',
        bgColor: 'bg-gray-100',
        description: 'Unknown permission level'
      };
  }
};
